import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  LayoutDashboard, 
  Calendar, 
  PlusCircle, 
  BarChart3, 
  ListChecks, 
  Activity,
  User,
  CreditCard,
  Link2,
  Shield,
  BookOpen,
  MessageCircle,
  LogOut,
  Menu,
  X,
  Bell,
  BellOff,
  GraduationCap
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import Avatar from './Avatar';
import FreemiumLockOverlay from './FreemiumLockOverlay';

const Layout = () => {
  const { user, logout } = useAuth();
  const { unseenCount, unseenCoachComments, unseenMessages, muted, setMuted } = useNotifications();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isSchool = user?.role === 'school';
  const isCustomPlanAthlete = (user?.subscription_plan || '').startsWith('custom_') && user?.subscription_status === 'premium';

  const navItems = isAdmin
    ? [
        { path: '/admin', icon: Shield, label: 'Panel Entrenador' },
        { path: '/admin/school', icon: GraduationCap, label: 'Grupos' },
        { path: '/admin/messages', icon: MessageCircle, label: 'Mensajes' },
        { path: '/library', icon: BookOpen, label: 'Biblioteca' },
        { path: '/profile', icon: User, label: 'Perfil' },
      ]
    : isSchool
    ? [
        { path: '/school', icon: GraduationCap, label: 'Mi Grupo' },
        { path: '/profile', icon: User, label: 'Perfil' },
      ]
    : [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/calendar', icon: Calendar, label: 'Calendario' },
        { path: '/log', icon: PlusCircle, label: 'Registrar' },
        { path: '/stats', icon: BarChart3, label: 'Estadísticas' },
        { path: '/plans', icon: ListChecks, label: 'Planes' },
        { path: '/zones', icon: Activity, label: 'Zonas' },
        { path: '/messages', icon: MessageCircle, label: 'Mensajes' },
        ...(isCustomPlanAthlete ? [{ path: '/library', icon: BookOpen, label: 'Biblioteca' }] : []),
        { path: '/connections', icon: Link2, label: 'Conexiones' },
        { path: '/profile', icon: User, label: 'Perfil' },
        { path: '/subscription', icon: CreditCard, label: 'Suscripción' },
      ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Mobile menu button — placed on the right side so it doesn't overlap
          the browser back arrow / native back gesture indicator on iOS/Android */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 p-2 rounded-lg bg-zinc-900 border border-zinc-800 shadow-lg"
        data-testid="mobile-menu-btn"
        aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-zinc-900/50 backdrop-blur-xl border-r border-zinc-800 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-800">
            <Link to={isAdmin ? '/admin' : isSchool ? '/school' : '/dashboard'} className="flex items-center gap-3">
              <img 
                src="/coach-logo-dark.png" 
                alt="Coach Alex Díaz" 
                className="w-12 h-12 object-contain"
              />
              <span className="font-black text-lg tracking-tight">Coach Alex Díaz</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const calendarBadge = item.path === '/calendar' ? (unseenCount + unseenCoachComments) : 0;
              const messagesBadge = item.path === '/messages' || item.path === '/admin/messages' ? unseenMessages : 0;
              const badgeCount = calendarBadge || messagesBadge;
              const showBadge = badgeCount > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${
                    isActive(item.path)
                      ? 'bg-[#ccff00] text-black font-semibold'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {showBadge && (
                    <span
                      className={`min-w-5 h-5 px-1.5 rounded-full text-[10px] font-black flex items-center justify-center ${
                        isActive(item.path) ? 'bg-black text-[#ccff00]' : 'bg-red-500 text-white'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase()}-badge`}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-3 mb-4 px-4">
              <Avatar src={user?.profile_photo} name={user?.name} size={40} className="bg-zinc-800" testId="sidebar-user-avatar" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={() => setMuted(!muted)}
              variant="ghost"
              className="w-full justify-start gap-3 text-zinc-400 hover:text-white hover:bg-zinc-800 mb-1"
              data-testid="mute-notifications-btn"
              title={muted ? 'Activar sonido de notificaciones' : 'Silenciar notificaciones'}
            >
              {muted ? <BellOff className="w-5 h-5 text-zinc-500" /> : <Bell className="w-5 h-5 text-[#ccff00]" />}
              <span>{muted ? 'Notificaciones silenciadas' : 'Notificaciones activas'}</span>
            </Button>
            <Button
              onClick={logout}
              variant="ghost"
              className="w-full justify-start gap-3 text-zinc-400 hover:text-red-400 hover:bg-red-400/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-[1600px] mx-auto p-6 lg:p-10">
          <Outlet />
        </div>
      </main>

      {/* Freemium gate — renders nothing for admins / premium / not enrolled */}
      <FreemiumLockOverlay />
    </div>
  );
};

export default Layout;
